/**
 * Route-level Error Boundary
 * Catches errors at the route level and allows navigation to other routes
 */

import { Component, ErrorInfo, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router'
import * as Sentry from '@sentry/react'
import { Icon } from '@/design-system/atoms/Icon'
import './ErrorBoundary.css'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

class RouteErrorBoundaryClass extends Component<
  Props & { navigate: (path: string) => void; location: { pathname: string } },
  State
> {
  constructor(
    props: Props & {
      navigate: (path: string) => void
      location: { pathname: string }
    }
  ) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Sentry with route context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
        route: {
          pathname: this.props.location.pathname,
        },
      },
    })
  }

  componentDidUpdate(
    prevProps: Props & {
      navigate: (path: string) => void
      location: { pathname: string }
    }
  ) {
    // Reset error state when navigating to a different route
    if (
      this.state.hasError &&
      prevProps.location.pathname !== this.props.location.pathname
    ) {
      this.setState({
        hasError: false,
        error: null,
      })
    }
  }

  handleGoHome = () => {
    this.setState(
      {
        hasError: false,
        error: null,
      },
      () => {
        this.props.navigate('/')
      }
    )
  }

  handleGoBack = () => {
    this.setState(
      {
        hasError: false,
        error: null,
      },
      () => {
        window.history.back()
      }
    )
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="route-error-boundary">
          <div className="error-content">
            <div className="error-icon">
              <Icon name="alert-triangle" size={32} />
            </div>
            <h1>Erro na Página</h1>
            <p>
              Ocorreu um erro ao carregar esta página. Você pode tentar voltar
              ou ir para a página inicial.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <details className="error-details">
                <summary>Detalhes do erro (apenas em desenvolvimento)</summary>
                <pre>{this.state.error.toString()}</pre>
              </details>
            )}

            <div className="error-actions">
              <button
                onClick={this.handleGoBack}
                className="btn btn-secondary"
                type="button"
              >
                ← Voltar
              </button>
              <button
                onClick={this.handleGoHome}
                className="btn btn-primary inline-flex items-center gap-2"
                type="button"
              >
                <Icon name="grid" size={16} />
                Ir para o início
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Wrapper component to use React Router hooks with class component
 */
export function RouteErrorBoundary({ children }: Props) {
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <RouteErrorBoundaryClass navigate={navigate} location={location}>
      {children}
    </RouteErrorBoundaryClass>
  )
}
